import clsx from 'clsx'
import type React from 'react'
import { forwardRef, useId } from 'react'

import { type IconProp } from 'slab/types'

interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  checkedIcon?: IconProp;
  color?: 'black' | 'green';
  description?: React.ReactNode;
  disabled?: boolean;
  label?: React.ReactNode;
  uncheckedIcon?: IconProp;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(({
  color = 'green',
  description,
  disabled,
  uncheckedIcon: UncheckedIcon,
  checkedIcon: CheckedIcon,
  id,
  label,
  ...props
}, forwardedRef) => {
  const autogeneratedId = useId()
  const descriptionId = useId()
  const inputId = id || autogeneratedId

  return (
    <div className={clsx('grid gap-x-4 gap-y-2', {
      'grid-cols-[1fr_auto]': label,
    })}
    >
      {!!label && (
        <label className='block col-start-1 body-bold text-gray-800' htmlFor={inputId}>
          {label}
        </label>
      )}
      <div
        className={clsx(
          'relative self-center',
          'rounded-full',
          '[--track-width:2.75rem] [--track-padding:0.125rem] [--handle-width:1.25rem]',
          'w-[--track-width]',
          'shadow-[0_0_0_0rem] has-[:hover]:shadow-[0_0_0_0.5rem] shadow-transparent has-[:hover]:shadow-gray-800/5 has-[:disabled]:has-[:hover]:shadow-transparent',
          'transition'
        )}
      >
        <input
          {...props}
          aria-describedby={description ? descriptionId : undefined}
          className='peer appearance-none absolute left-0 top-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-default'
          disabled={disabled}
          id={inputId}
          ref={forwardedRef}
          role='switch'
          type='checkbox'
        />
        {/* Track */}
        <div
          className={clsx(
            'group/track',
            'rounded-full p-[--track-padding]',
            'peer-disabled:opacity-50',
            'outline outline-offset-2 outline-2 outline-transparent peer-focus-visible:outline-thatch-500',
            'shadow-default peer-disabled:shadow-none',
            'transition-colors',
            {
              'bg-gray-400 peer-checked:bg-gray-800': color === 'black',
              'bg-gray-400 peer-checked:bg-green-400': color === 'green',
            }
          )}
        >
          {/* Handle */}
          <div
            className={clsx(
              'pointer-events-none',
              'w-[--handle-width] h-[--handle-width]',
              'rounded-full',
              'p-px bg-clip-content',
              'flex items-center justify-center',
              'bg-white',
              'border-gradient-to-b from-gray-300 to-gray-500',
              'text-gray-500',
              'translate-x-0 peer-checked:group-[]/track:translate-x-[calc(var(--track-width)-var(--handle-width)-(var(--track-padding)*2))]',
              'transition'
            )}
          >
            {UncheckedIcon && (
              <div className='visible peer-checked:group-[]/track:hidden'>
                <UncheckedIcon aria-hidden size={12} />
              </div>
            )}
            {CheckedIcon && (
              <div className='hidden peer-checked:group-[]/track:block'>
                <CheckedIcon aria-hidden size={12} />
              </div>
            )}
          </div>
        </div>
      </div>
      {!!label && !!description && (
        <div className='col-start-1 row-start-2 body-sm text-gray-500' id={descriptionId}>
          {description}
        </div>
      )}
    </div>
  )
})
